import { DOCUMENT, isPlatformBrowser, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { DynamicHooksModule } from 'ngx-dynamic-hooks';
import { Subject } from 'rxjs';
import { AnalyticsService } from 'analytics';
import { UserService } from 'auth-data-access';
import { SvgComponent } from 'icon';
import { Modal, RawLink, Style } from 'interfaces';
import { LinkComponent } from 'link';
import { ModalsService } from 'modal';
import { BrowserService } from 'utils';
import { ModalsSlice } from './modals.slice';

@Component({
  selector: 'app-modals',
  templateUrl: './modals.component.html',
  styleUrls: ['./modals.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgFor, NgClass, MatDialogModule, NgStyle, NgIf, SvgComponent, DynamicHooksModule, LinkComponent],
})
export class ModalsComponent implements OnInit, AfterViewInit, OnDestroy {
  private modalsService = inject(ModalsService);
  private userService = inject(UserService);
  private browser = inject(BrowserService);
  private renderer = inject(Renderer2);
  private analytics = inject(AnalyticsService);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  isLoggedIn = false;
  @Input() slice: ModalsSlice;
  @ViewChildren('modals') modals: QueryList<TemplateRef<unknown>>;
  private _destroyed = new Subject<void>();

  ngOnInit() {
    this.userService.isAuthenticated().subscribe(authRes => (this.isLoggedIn = authRes));
  }

  ngAfterViewInit() {
    if (this.slice.modals.length) {
      isPlatformBrowser(this.platformId) && this.modalsService.registerModals(this.slice.modals, this.modals);
      this.setAutoOpen();
    }
  }

  ngOnDestroy() {
    isPlatformBrowser(this.platformId) && this.modalsService.unregisterModals();
    this._destroyed.next();
    this._destroyed.complete();
  }

  private setAutoOpen() {
    this.slice.modals.forEach(
      ({ id, showAutomaticallyAfter, showOnDevices, hide_to_logged_in_users, hide_to_not_logged_in_users }) => {
        const showModal = this.isLoggedIn ? !hide_to_logged_in_users : !hide_to_not_logged_in_users;
        if (showModal) {
          this.modalsService.executeFnForDevice(() => {
            if (showAutomaticallyAfter > 0) {
              setTimeout(() => {
                this.modalsService.openModal(id);
              }, showAutomaticallyAfter * 1000);
            }
          }, showOnDevices);
        }
      }
    );
  }

  getModalHeaderClass(modal: Modal) {
    return `text-${modal.titleAndSubtitleAlignment}`;
  }

  getModalImgAlignmentClass(modal: Modal) {
    if (modal.imgSize === 'cover') {
      return 'img-size-cover';
    } else {
      return `img-align-${modal.imgAlignment}`;
    }
  }

  getModalImgSizes(modal: Modal) {
    if (modal.imgSize === 'normal' && modal.imgStaticWidth) {
      return `${modal.imgStaticWidth}px`;
    }

    return '(min-width: 900px) 720px, 80vw';
  }

  onLinkClick(modal: Modal) {
    if (modal.name !== '') {
      const data = {
        event: 'gtm visitor interaction',
        interaction_name: 'offer popup click',
        popup_offer: modal.name,
      };
      this.analytics.customEvent(data);
    }
  }

  getLinkStyle(openModalCtaLink: RawLink) {
    const result: Style[] = [];

    if (openModalCtaLink.style_bold) {
      result.push('bold');
    }

    if (openModalCtaLink.style_underline) {
      result.push('underline');
    }

    return result;
  }

  isLinkVisible(link: RawLink) {
    if ((link.hide_to_logged_in_users && this.isLoggedIn) || (link.hide_to_not_logged_in_users && !this.isLoggedIn)) {
      return false;
    }

    return true;
  }
}
